import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ApiError, get, withQuery } from "../../api/client";
import { useSwapStop, type PlanStop } from "../../api/routing";
import type { SiteRow } from "../../api/sites";

/** A priority swap on one pending stop: another site in the stop's cluster
 * takes its slot, with a reason. The old site stays owed a visit. The server
 * refuses a site the scheduling gate holds, and says why. */
export default function SwapStop({ planId, stop, onDone }: { planId: number; stop: PlanStop; onDone: () => void }) {
  const swap = useSwapStop(planId);
  const clusterId = stop.site?.cluster_id ?? null;
  const options = useQuery<{ rows: SiteRow[]; total: number }, ApiError>({
    queryKey: ["sites", "swap", clusterId],
    // The register's largest page (`_PAGE_MAX`): a cluster is a few dozen sites,
    // and the notice below says so if one ever holds more.
    queryFn: () => get(withQuery("/api/v1/sites", { cluster_id: clusterId, limit: SWAP_PICKER_LIMIT })),
    enabled: clusterId !== null,
  });
  const shown = options.data?.rows.length ?? 0;
  const total = options.data?.total ?? 0;
  const [siteId, setSiteId] = useState("");
  const [reason, setReason] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    swap.mutate({ stopId: stop.id, site_id: Number(siteId), reason: reason.trim() }, { onSuccess: onDone });
  }
  return (
    <form className="plan-stop-form" onSubmit={submit} aria-label={`Swap stop ${stop.seq}`}>
      <label className="field">
        Swap in
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
          <option value="">Choose a site in this cluster…</option>
          {(options.data?.rows ?? [])
            .filter((row) => row.id !== stop.site?.id)
            .map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
        </select>
      </label>
      <label className="field">
        Why it comes first
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
      </label>
      <button type="submit" className="btn btn-primary" disabled={swap.isPending || siteId === "" || reason.trim() === ""}>
        Swap
      </button>
      {total > shown ? (
        <p className="notice warn">
          The first {shown} of the cluster's {total} sites are listed.
        </p>
      ) : null}
      {options.isError ? <p className="notice bad">The cluster's sites could not be loaded: {options.error.message}</p> : null}
      {swap.isError ? <p className="notice bad">{swap.error.message}</p> : null}
    </form>
  );
}

/** The register's largest page; a cluster with more sites says so on the form. */
export const SWAP_PICKER_LIMIT = 1000;

/** Whether a stop's outcome can still be changed on the plan screen: not on a
 * plan the office can no longer edit, and not on a stop a priority swap
 * replaced, whose outcome is the swap's (the server refuses it,
 * `plans.SwappedOut`); its replacement carries the visit. */
export function canEditStop(planEditable: boolean, stop: PlanStop): boolean {
  return planEditable && !stop.swapped_out_for;
}

/** Both halves of a priority swap, as the stop's trail names them; nothing on a
 * stop no swap touched. Distinct from `replaces` / `replaced_by`, which link a
 * missed visit to the later plan that repeats it. */
export function SwapTrail({ stop }: { stop: PlanStop }) {
  return (
    <>
      {stop.swapped_in_for ? <span className="plan-stop-trail">Swapped in for {stop.swapped_in_for.site_name}</span> : null}
      {stop.swapped_out_for ? <span className="plan-stop-trail">Swapped out for {stop.swapped_out_for.site_name}</span> : null}
    </>
  );
}
